import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { AiChatService } from '../ai-chat.service';
import { ChatMessage, ChatTab } from '../ai-chat.types';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-ai-chat-standalone',
  templateUrl: './ai-chat-standalone.component.html',
  styleUrls: ['./ai-chat-standalone.component.scss']
})
export class AiChatStandaloneComponent implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit, OnChanges {
  chatTabs: ChatTab[] = [];
  activeChatId: string = '';
  isMinimized: boolean = true;
  isLoading: boolean = false;
  currentMessage: string = '';
  showQuickActions: boolean = false;
  tutorialHandledExternally: boolean = false;
  
  @ViewChild('messagesContainer') messagesContainer!: ElementRef;
  
  private subscriptions: Subscription = new Subscription();
  private shouldScrollToBottom = false;
  private _lastMessageCount = 0;

  constructor(private aiChatService: AiChatService) {}

  	ngOnInit() {
		// Add class to body for transparent background when embedded
		document.body.classList.add('ai-chat-embed');
		document.documentElement.classList.add('ai-chat-embed');
		
		// Clear any existing tutorial state when running in iframe
		this.clearTutorialState();
		
		// Listen for messages from parent window
		window.addEventListener('message', this.handleParentMessage.bind(this));
		
		this.loadChatsFromStorage();
		
		// If no chats exist, create a default one
		if (this.chatTabs.length === 0)
		{
			this.createNewChat();
		}
		else
		{
			this.activeChatId = this.chatTabs[0].id;
		}
		
		// Send initial state to parent and check for tutorial system
		this.sendStateToParent();
		this.checkTutorialSystem();
	}

	ngOnDestroy() {
		// Remove classes when component is destroyed
		document.body.classList.remove('ai-chat-embed');
		document.documentElement.classList.remove('ai-chat-embed');
		
		// Remove event listener
		window.removeEventListener('message', this.handleParentMessage.bind(this));
		
		this.subscriptions.unsubscribe();
		this.saveChatsToStorage();
	}

	ngAfterViewInit() {
		// Initial scroll to bottom when the view is ready
		this.forceScrollToBottom();
	}

	ngAfterViewChecked() {
		// If flagged, scroll
		if (this.shouldScrollToBottom)
		{
			this.scrollToBottom();
			this.shouldScrollToBottom = false;
		}

		// Detect new messages and auto-scroll
		const activeChat = this.getActiveChat();
		const currentCount = activeChat ? (activeChat.messages?.length || 0) : 0;
		if (currentCount !== this._lastMessageCount)
		{
			// Use setTimeout to ensure DOM updates are complete
			setTimeout(() => this.scrollToBottom(), 0);
			this._lastMessageCount = currentCount;
		}
	}

	ngOnChanges(changes: SimpleChanges) {
		// Update message count tracking when chat changes
		const activeChat = this.getActiveChat();
		const count = activeChat ? (activeChat.messages?.length || 0) : 0;
		this._lastMessageCount = count;
		// Defer to next tick so DOM updates complete
		setTimeout(() => this.forceScrollToBottom());
	}

	private handleParentMessage(event: MessageEvent) {
		if (event.data && event.data.type === 'ai-chat-parent') {
			switch(event.data.action) {
				case 'requestState':
					this.sendStateToParent();
					break;
			}
		}
		
		// Handle tutorial events from parent
		if (event.data && event.data.type === 'tutorial-event-from-parent') {
			this.handleTutorialEvent(event.data.data);
		}
		
		// Handle tutorial system responses
		if (event.data && event.data.type === 'tutorial-started-confirmation') {
			this.addSystemMessage('✅ Interactive tutorial started! Look for highlighted elements on the page.');
		}
		
		if (event.data && event.data.type === 'tutorial-error') {
			this.addSystemMessage('❌ Tutorial system unavailable: ' + event.data.message);
		}
		
		// Handle tutorial handled externally
		if (event.data && event.data.type === 'tutorial-handled-externally') {
			// Set flag to prevent showing tutorials in iframe
			this.tutorialHandledExternally = true;
			console.log('Tutorial will be handled by parent page');
		}
		
		// Handle tutorial fallback response
		if (event.data && event.data.type === 'tutorial-fallback-response') {
			this.addSystemMessage(event.data.message);
		}
		
		// Handle tutorial system availability response
		if (event.data && event.data.type === 'tutorial-system-available') {
			if (event.data.available) {
				console.log('Tutorial system is available in parent page');
				this.tutorialHandledExternally = true;
			} else {
				console.log('Tutorial system not available in parent page:', event.data.error);
				this.tutorialHandledExternally = false;
			}
		}
	}

	// Handle tutorial events and update chat accordingly
	private handleTutorialEvent(eventData: any) {
		const { eventType, data } = eventData;
		
		switch(eventType) {
			case 'tutorial_started':
				this.addSystemMessage(`🎯 Tutorial started: ${data.tutorialTitle || 'Interactive guidance'}`);
				break;
			case 'tutorial_completed':
				this.addSystemMessage(`✅ Tutorial completed! Great job!`);
				break;
			case 'step_completed':
				this.addSystemMessage(`✓ Step completed: ${data.stepData?.title || 'Step'}`);
				break;
			case 'elements_not_found':
				this.addSystemMessage(`⚠️ I couldn't find those elements on the page. You might need to navigate to a different section first.`);
				break;
			case 'dynamic_tutorial_generated':
				this.addSystemMessage(`🤖 Generated custom tutorial with ${data.stepCount} steps based on current page analysis.`);
				break;
			case 'page_analysis_complete':
				this.addSystemMessage(`📊 Page analysis complete: Found ${data.elementCount} interactive elements.`);
				break;
			case 'tutorial_error':
				this.addSystemMessage(`❌ Tutorial error: ${data.message || 'Unable to generate tutorial for current page.'}`);
				break;
		}
	}

	// Add system message to current chat
	private addSystemMessage(content: string) {
		const activeChat = this.getActiveChat();
		if (activeChat) {
			const systemMessage: ChatMessage = {
				id: this.generateMessageId(),
				content: content,
				role: 'assistant',
				timestamp: new Date(),
				isSystem: true
			};
			activeChat.messages.push(systemMessage);
			this.saveChatsToStorage();
		}
	}

	private sendStateToParent() {
		// Send current state to parent window if in iframe
		if (window.parent && window.parent !== window) {
			window.parent.postMessage({
				type: 'ai-chat',
				action: this.isMinimized ? 'minimize' : 'expand'
			}, '*');
		}
	}

	toggleWidget() {
		this.isMinimized = !this.isMinimized;
		this.sendStateToParent();
	}	createNewChat() {
		const now = new Date();
		const timeString = now.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
		const newChat: ChatTab = {
			id: this.generateChatId(),
			title: `New Chat ${timeString}`,
			messages: [],
			createdAt: now,
			updatedAt: now
		};
		
		this.chatTabs.push(newChat);
		this.activeChatId = newChat.id;
		this.saveChatsToStorage();
	}  selectChat(chatId: string) {
    this.activeChatId = chatId;
  }

  closeChat(chatId: string, event: Event) {
		event.stopPropagation();
		
		const index = this.chatTabs.findIndex(chat => chat.id === chatId);
		if (index !== -1)
		{
			this.chatTabs.splice(index, 1);
			
			// If we closed the active chat, select another one
			if (this.activeChatId === chatId)
			{
				if (this.chatTabs.length > 0)
				{
					this.activeChatId = this.chatTabs[0].id;
				}
				else
				{
					this.createNewChat();
				}
			}
			
			this.saveChatsToStorage();
		}
	}

	startEditingTabTitle(chatId: string, event: Event) {
		event.stopPropagation();
		const chat = this.chatTabs.find(c => c.id === chatId);
		if (chat)
		{
			chat.isEditing = true;
		}
	}

	finishEditingTabTitle(chatId: string, newTitle: string) {
		const chat = this.chatTabs.find(c => c.id === chatId);
		if (chat)
		{
			chat.isEditing = false;
			if (newTitle.trim())
			{
				chat.title = newTitle.trim();
				chat.updatedAt = new Date();
				this.saveChatsToStorage();
			}
		}
	}

	cancelEditingTabTitle(chatId: string) {
		const chat = this.chatTabs.find(c => c.id === chatId);
		if (chat)
		{
			chat.isEditing = false;
		}
	}

	onTabTitleKeyPress(event: KeyboardEvent, chatId: string) {
		if (event.key === 'Enter')
		{
			event.preventDefault();
			const input = event.target as HTMLInputElement;
			this.finishEditingTabTitle(chatId, input.value);
		}
		else if (event.key === 'Escape')
		{
			event.preventDefault();
			this.cancelEditingTabTitle(chatId);
		}
	}

  async sendMessage() {
    if (!this.currentMessage.trim() || this.isLoading) {
      return;
    }

    const activeChat = this.getActiveChat();
    if (!activeChat) {
      return;
    }

    const userMessage: ChatMessage = {
      id: this.generateMessageId(),
      content: this.currentMessage,
      role: 'user',
      timestamp: new Date()
    };

    activeChat.messages.push(userMessage);
    activeChat.updatedAt = new Date();
    
    const messageToSend = this.currentMessage;
    this.currentMessage = '';
    this.isLoading = true;
    this.shouldScrollToBottom = true; // Trigger auto-scroll

    // Check if this is a tutorial request
    const isTutorialRequest = this.detectTutorialRequest(messageToSend);
    
    try {
      // Send to AI service
      const response = await this.aiChatService.sendMessage(messageToSend, activeChat.id, {
        previousMessages: activeChat.messages.slice(-10) // Send last 10 messages for context
      }).toPromise();

      if (response.success) {
        const aiMessage: ChatMessage = {
          id: this.generateMessageId(),
          content: response.message,
          role: 'assistant',
          timestamp: new Date(),
          toolCalls: response.toolCalls
        };

        activeChat.messages.push(aiMessage);
        activeChat.updatedAt = new Date();
        
        // Update chat title if it's the first exchange
        if (activeChat.messages.length === 2) {
          activeChat.title = this.generateChatTitle(userMessage.content);
        }

        // If this was a tutorial request, send to tutorial system
        if (isTutorialRequest) {
          this.handleTutorialRequest(messageToSend, response);
        }
      } else {
        const errorMessage: ChatMessage = {
          id: this.generateMessageId(),
          content: response.message || 'I apologize, but I encountered an error. Please try again.',
          role: 'assistant',
          timestamp: new Date(),
          isError: true
        };
        activeChat.messages.push(errorMessage);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: ChatMessage = {
        id: this.generateMessageId(),
        content: 'I apologize, but I encountered a connection error. Please check your internet connection and try again.',
        role: 'assistant',
        timestamp: new Date(),
        isError: true
      };
      activeChat.messages.push(errorMessage);
    } finally {
      this.isLoading = false;
      this.saveChatsToStorage();
    }
  }

  // Detect if user message is requesting tutorial help
  private detectTutorialRequest(message: string): boolean {
    const tutorialKeywords = [
      'how to', 'how do i', 'help me', 'guide me', 'show me', 'tutorial', 
      'walk me through', 'step by step', 'enroll', 'create user', 'add user',
      'create course', 'safety report', 'where is', 'find', 'locate',
      'fill out', 'form', 'button', 'click', 'submit', 'save'
    ];
    
    const lowerMessage = message.toLowerCase();
    return tutorialKeywords.some(keyword => lowerMessage.includes(keyword));
  }

  // Enhanced tutorial request handling with dynamic generation support
  private handleTutorialRequest(userMessage: string, aiResponse: any) {
    // Check if tutorials are being handled externally (by parent page)
    if (this.tutorialHandledExternally) {
      console.log('Tutorial will be handled by parent page, skipping iframe tutorial');
      return;
    }
    
    // Send tutorial request to parent window (which will handle it via embed script)
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({
        type: 'ai-chat',
        action: 'tutorial-request',
        userMessage: userMessage,
        aiResponse: aiResponse.message,
        requestType: this.determineTutorialType(userMessage),
        context: {
          currentUrl: window.location.href,
          timestamp: new Date().toISOString(),
          chatId: this.activeChatId
        }
      }, '*');
      
      // Add a message to the chat indicating tutorial is starting
      const activeChat = this.getActiveChat();
      if (activeChat) {
        const tutorialMessage: ChatMessage = {
          id: this.generateMessageId(),
          content: '🎯 Analyzing page and generating interactive tutorial... Look for highlighted elements!',
          role: 'assistant',
          timestamp: new Date(),
          isSystem: true
        };
        activeChat.messages.push(tutorialMessage);
        this.saveChatsToStorage();
      }
    } else {
      // If not in iframe, try to use tutorial system directly (fallback)
      if ((window as any).AITutorialAgent) {
        (window as any).AITutorialAgent.handleAITutorialRequest({
          userMessage: userMessage,
          intent: 'start_tutorial',
          context: {}
        });
      }
    }
  }

  // Determine what type of tutorial the user is requesting
  private determineTutorialType(userMessage: string): string {
    const message = userMessage.toLowerCase();
    
    if (message.includes('form') || message.includes('fill out') || message.includes('enter')) {
      return 'form_guidance';
    }
    if (message.includes('button') || message.includes('click') || message.includes('where is')) {
      return 'element_finding';
    }
    if (message.includes('create') || message.includes('add') || message.includes('new')) {
      return 'creation_workflow';
    }
    if (message.includes('edit') || message.includes('update') || message.includes('modify')) {
      return 'editing_workflow';
    }
    if (message.includes('save') || message.includes('submit') || message.includes('send')) {
      return 'submission_workflow';
    }
    
    return 'general_guidance';
  }

  // Toggle quick actions panel
  toggleQuickActions() {
    this.showQuickActions = !this.showQuickActions;
  }

  // Handle quick tutorial request
  quickTutorialRequest(message: string) {
    this.currentMessage = message;
    this.showQuickActions = false;
    this.sendMessage();
  }

  getActiveChat(): ChatTab | undefined {
    return this.chatTabs.find(chat => chat.id === this.activeChatId);
  }

  onKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  clearChat() {
    const activeChat = this.getActiveChat();
    if (activeChat) {
      activeChat.messages = [];
      activeChat.updatedAt = new Date();
      this.saveChatsToStorage();
    }
  }

  exportChat() {
    const activeChat = this.getActiveChat();
    if (activeChat) {
      const chatData = {
        title: activeChat.title,
        createdAt: activeChat.createdAt,
        messages: activeChat.messages
      };
      
      const blob = new Blob([JSON.stringify(chatData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeChat.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }

  private generateChatId(): string {
    return 'chat_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  private generateMessageId(): string {
    return 'msg_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

	private generateChatTitle(firstMessage: string): string {
		// Clean up the message and extract meaningful content
		const cleanMessage = firstMessage.trim().replace(/\s+/g, ' ');
		
		// If it's a question, try to extract the main topic
		if (cleanMessage.includes('?'))
		{
			const questionPart = cleanMessage.split('?')[0];
			if (questionPart.length > 5)
			{
				return questionPart.length > 35 ? questionPart.substring(0, 32) + '...' : questionPart + '?';
			}
		}
		
		// Look for common patterns and create smart titles
		const lowerMessage = cleanMessage.toLowerCase();
		
		if (lowerMessage.startsWith('help') || lowerMessage.startsWith('how'))
		{
			return cleanMessage.length > 40 ? cleanMessage.substring(0, 37) + '...' : cleanMessage;
		}
		
		if (lowerMessage.startsWith('what') || lowerMessage.startsWith('why') || lowerMessage.startsWith('when'))
		{
			return cleanMessage.length > 40 ? cleanMessage.substring(0, 37) + '...' : cleanMessage;
		}
		
		if (lowerMessage.startsWith('explain') || lowerMessage.startsWith('describe'))
		{
			return cleanMessage.length > 40 ? cleanMessage.substring(0, 37) + '...' : cleanMessage;
		}
		
		// For statements or other content, take the first meaningful part
		const words = cleanMessage.split(' ');
		if (words.length <= 6)
		{
			return cleanMessage.length > 40 ? cleanMessage.substring(0, 37) + '...' : cleanMessage;
		}
		
		// Take first 6 words for longer messages
		const shortTitle = words.slice(0, 6).join(' ');
		return shortTitle + '...';
	}  private loadChatsFromStorage() {
    try {
      const saved = localStorage.getItem('ai_chat_tabs');
      if (saved) {
        this.chatTabs = JSON.parse(saved).map((chat: any) => ({
          ...chat,
          createdAt: new Date(chat.createdAt),
          updatedAt: new Date(chat.updatedAt),
          messages: chat.messages.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }))
        }));
      }
    } catch (error) {
      console.error('Error loading chats from storage:', error);
      this.chatTabs = [];
    }
  }

  private saveChatsToStorage() {
    try {
      localStorage.setItem('ai_chat_tabs', JSON.stringify(this.chatTabs));
    } catch (error) {
      console.error('Error saving chats to storage:', error);
    }
  }

  // Track by functions for performance
  trackByFn(index: number, item: ChatTab): string {
    return item.id;
  }

  trackByMessageFn(index: number, item: ChatMessage): string {
    return item.id;
  }

  // Check if tutorial system is available and handle accordingly
  private checkTutorialSystem() {
    // If we're in an iframe, ask parent if tutorial system is available
    if (window.parent && window.parent !== window) {
      // Wait a bit for parent to load, then check
      setTimeout(() => {
        window.parent.postMessage({
          type: 'ai-chat',
          action: 'check-tutorial-system'
        }, '*');
      }, 1000);
    }
  }

  // Clear any existing tutorial state when running in embedded mode
  private clearTutorialState() {
    try {
      // Clear tutorial-related localStorage items
      const tutorialKeys = [
        'interactive_tutorial_current',
        'interactive_tutorial_state',
        'tutorial_progress',
        'current_tutorial_id'
      ];
      
      tutorialKeys.forEach(key => {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
          console.log(`Cleared tutorial state: ${key}`);
        }
      });

      // If tutorial systems are loaded in this context, end any active tutorials
      if ((window as any).InteractiveTutorial) {
        (window as any).InteractiveTutorial.endTutorial();
      }
    } catch (error) {
      console.log('Error clearing tutorial state:', error);
    }
  }

  // Scrolling methods for auto-scroll functionality
  private scrollToBottom() {
    if (this.messagesContainer) {
      const container = this.messagesContainer.nativeElement;
      // Always scroll to bottom when new messages arrive
      container.scrollTop = container.scrollHeight;
    }
  }

  private forceScrollToBottom() {
    // Force scroll regardless of prior state
    if (this.messagesContainer)
    {
      const container = this.messagesContainer.nativeElement;
      container.scrollTop = container.scrollHeight;
    }
  }
}
